import { createLog } from '@iv/shared/src/log';
import events from 'events';
import dataProcessor from './dataProcessor';
import PlayerBridge, { IPlayerInfo } from '@iv/adapter-player/src/interface';
import AdapterBridge from '@iv/adapter-bridge/src/interface';
import { SLA } from '@iv/shared/src/reportSla';
import { methodTracker, trackerEnv } from '@iv/shared/src/tracker';
import { IParentBridge } from '../MessageChannel';

const log = createLog('Engine');

interface EngineInitParam {
  playerInfo: IPlayerInfo;
  dataProcessor: dataProcessor;
  ivMsg: events.EventEmitter;
  parentBridge: IParentBridge;
  adapterBridge?: AdapterBridge;
  useLocalMode?: boolean;
  businessId?: string;
  fetchLib?: Function;
}

/**
 * 命名空间engine
 * @example
 * ```javascript
 * // 在组件内的使用方式
 *
 * // 组件监听事件
 * this.bridge.on('engine', 'event', () => {});
 *
 * // action为方法名
 * // param为action方法的参数
 * const param = { expression: 'v1=v1+1' };
 * this.bridge.invoke('engine', 'action', param);
 * ```
 */
class Interactor {
  /**
   * @description 视频播放事件
   * @param videoInfo {@link IVideoInfo}
   * @example
   * ```javascript
   * this.bridge.on('engine', 'videoPlay', (videoInfo: IVideoInfo) => {});
   * ```
   * @event
   */
  public static videoPlay: string;

  /**
   * @description 视频切换事件
   * @param videoInfo {@link IVideoInfo}
   * @example
   * ```javascript
   * this.bridge.on('engine', 'videoNodeChange', (videoInfo: IVideoInfo) => {});
   * ```
   * @event
   */
  public static videoNodeChange: string;

  /**
   * @description 数值更新事件
   * @internal
   */
  public static vUpdate: string;

  /**
   * @description 互动信息更新事件
   * @param node {@link ICfgNode}
   * @example
   * ```javascript
   * this.bridge.on('engine', 'interactInfoUpdate', (node: ICfgNode) => {});
   * ```
   * @event
   */
  public static interactInfoUpdate: string;

  /**
   * @description 视频播放事件更新事件
   * @param time 当前播放时间
   * @example
   * ```javascript
   * this.bridge.on('engine', 'videoTimeUpdate', (time: number) => {});
   * ```
   * @event
   */
  public static videoTimeUpdate: string;

  /**
   * 本地模式，若是，v数值、kv存储，都不请求后台
   * @internal
   */
  public isLocal: boolean;

  /**
   * @internal
   */
  public msg: events.EventEmitter;

  /**
   * @internal
   */
  public ivMsg: events.EventEmitter;

  private player: PlayerBridge;
  private bridge: AdapterBridge;
  private dataProcessor: dataProcessor;
  private currentNode: ICfgNode;
  private isFirstTime = true;
  private playerInfo: IPlayerInfo;
  private parentBridge: IParentBridge;
  private localKeyValue: { [key: string]: string } = {};
  private fetchLib: Function;
  private preloadTimeout: number;

  /**
   * @internal
   */
  public constructor({
    playerInfo,
    dataProcessor,
    ivMsg,
    parentBridge,
    adapterBridge,
    useLocalMode,
    businessId,
    fetchLib,
  }: EngineInitParam) {
    this.player = playerInfo.player;
    this.dataProcessor = dataProcessor;
    this.msg = new events.EventEmitter();
    this.ivMsg = ivMsg;
    this.playerInfo = playerInfo;
    this.parentBridge = parentBridge;
    this.bridge = adapterBridge;
    this.isLocal = useLocalMode || false;
    this.fetchLib = fetchLib;

    if (businessId) {
      const sla = new SLA(businessId);
      this.reportSla = sla.reportSla.bind(sla);
    }
  }

  public reportLog(data: any) {
    log(data);
  }

  /**
   * @internal
   */
  public reportSla(data: { id: string; value: number }) {
    console.log('method reportSla does not implements. args: ', data);
  }

  /**
   * @internal
   */
  public async ready() {
    const nodeInfo = await this.dataProcessor.getNodeInfo();
    this.player.play(nodeInfo.uri);
  }

  /**
   * 通过action方法，执行配置的行为动作
   * @param action.jump 跳转的视频id
   * @param action.expression 执行的表达式
   * @example
   * ```javascript
   * const param = { expression: 'v1=v1+1' };
   * this.bridge.invoke('engine', 'action', param);
   * ```
   */
  public async action(action: IAction) {
    log('action', action);
    const { expression, jump } = action;
    if (expression) {
      log('expression', expression);
      if (this.isLocal) {
        this.msg.emit('vUpdate', {
          expression,
        });
      } else {
        try {
          // 调用 bridge 接口修改数值
          const { result } = await this.bridge.excuteVExpression({
            nodeId: this.currentNode.id,
            time: (await this.player.getPlayTime()).result,
            expression,
          });
          this.msg.emit('vUpdate', {
            numbers: result,
          });
        } catch (e) {
          log('expression update fail', expression, e);
        }
      }
    }
    if (jump) {
      log('nextNode', jump);
      const nodeInfo = await this.dataProcessor.getNodeInfo(jump);
      this.player.setNext(nodeInfo.uri, {
        intterupt: true,
        startTime: nodeInfo?.startTime,
      });
    }
  }

  /**
   * @description 事件监听
   * @param eventName 事件名
   * @param callback 回调函数
   */
  public on(eventName: string, callback: (...args: any[]) => void) {
    this.msg.on(eventName, callback);
  }

  /**
   * 初始化
   * @internal
   */
  @methodTracker(false, trackerEnv.parent)
  public init() {
    log('[初始化]绑定事件');
    const vnchange = async (videoInfo: IVideoInfo) => {
      log('[流程]事件触发videoNodeChange:', videoInfo);

      if (this.isFirstTime) {
        this.ivMsg.emit('beginPlay', videoInfo);
        this.isFirstTime = false;
      }

      const nodeChangeStartTime = Number(new Date());
      this.msg.emit('videoNodeChange', videoInfo);
      const nodeChangeEndTime = Number(new Date());

      this.currentNode = await this.dataProcessor.findNodeInfo(videoInfo);
      this.currentNode.nodeChangeStartTime = nodeChangeStartTime;
      this.currentNode.nodeChangeEndTime = nodeChangeEndTime;

      this.msg.emit('interactInfoUpdate', this.currentNode);
      this.preloadVideo();
    };
    this.msg.on('videoPlay', vnchange);
    this.player.on('videoNodeChange', vnchange);
    this.player.on('timeupdate', (time: number) => {
      this.timeUpdate(time);
      this.msg.emit('videoTimeUpdate', time);
    });
    // 播放器事件逻辑
    this.bindPlayer(this.playerInfo.canAutoplay, this.playerInfo.isPlaying);

    // 更新v数值
    this.msg.emit('vUpdate', {
      expression: '',
      numbers: this.dataProcessor.getVariableList(),
    });
  }

  /**
   * 展示互动层
   * @internal
   */
  public showInteract(forceShow?: boolean) {
    this.parentBridge.show();
    this.ivMsg.emit('showInteract');
    this.bridge.showInteract(forceShow);
    if (this.player) {
      this.player.hideControl();
    }
  }

  /**
   * 隐藏互动层
   * @internal
   */
  public hideInteract() {
    this.parentBridge.hide();
    this.ivMsg.emit('hideInteract');
    this.bridge.hideInteract();
  }

  /**
   * 更新某个组件的prop
   * @internal 编辑模式需要，无需输出api
   */
  public updateCompProp(id: string, propData: any) {
    this.msg.emit('propUpdate', {
      id,
      data: propData,
    });
  }

  /**
   * 新增某个组件
   * @internal 编辑模式需要，无需输出api
   */
  public addComp(id: string, componentId: string, propData: any) {
    this.msg.emit('compAdded', {
      id,
      componentId,
      data: propData,
    });
  }

  /**
   * 删除某个组件
   * @internal 编辑模式需要，无需输出api
   */
  public removeComp(id: string) {
    this.msg.emit('compRemoved', {
      id,
    });
  }

  /**
   * 更新某个组件的prop
   * @internal 编辑模式需要，无需输出api
   */
  public updateInteract(interact: ICfgInteract) {
    this.msg.emit('propUpdate', {
      id: interact.id,
      data: interact.interactConfig,
    });
  }

  /**
   * 新增某个组件
   * @internal 编辑模式需要，无需输出api
   */
  public addInteract(interact: ICfgInteract, template: ICfgComponent) {
    this.msg.emit('interactAdded', {
      interact,
      template,
    });
  }

  /**
   * 删除某个组件
   * @internal 编辑模式需要，无需输出api
   */
  public removeInteract(id: string) {
    this.msg.emit('compRemoved', {
      id,
    });
  }

  /**
   * 获取当前播放节点信息
   * @internal
   */
  public getCurrentNode() {
    return this.currentNode;
  }

  /**
   * 读 Key-Value 存储
   * @param keys 需要获取的 Key 的列表
   * @example
   * ```javascript
   * // 假设 Key 为 name 的存储，Value 为 iv
   * const name = await this.bridge.invoke('engine', 'getString', ['name']);
   * // name == 'iv'
   * ```
   */
  public async getString(keys: Array<string>) {
    if (this.isLocal) {
      log('读取kv', keys);
      const textValues: { [key: string]: string } = {};
      keys.forEach((key) => {
        textValues[key] = this.localKeyValue[key] || '';
      });
      return textValues;
    }
    const { result } = await this.bridge.getString(keys, this.dataProcessor.getMetaId());
    return result;
  }

  /**
   * 写 Key-Value 存储
   * @param kv 需要设置的 Key-Value 对象
   * @example
   * ```javascript
   * // 假设需要把 Key 为 name 的存储的 Value 设置为 iv1
   * const result = await this.bridge.invoke('engine', 'setString', {
   *   key: 'name',
   *   value: 'iv1'
   * });
   * ```
   */
  public async setString(kv: { key: string; value: string }) {
    const { key, value } = kv;
    if (this.isLocal) {
      this.localKeyValue[key] = value;
      return {};
    }
    const { result } = await this.bridge.setString(kv, this.dataProcessor.getMetaId());
    return result;
  }

  /**
   * 组件使用网络请求
   * @param params 如果 new InteractVideo({
   *  ...
   *  fetchLib: axios
   * })
   * 则参照 axios api
   *
   * @example
   * ```javascript
   *  // 在互动组件内
   *  const res = await this.bridge.invoke('engine', 'fetch', {
   *    method: 'post',
   *    url: 'https://api.weishi.qq.com/trpc...',
   *    data: {
   *      msg: '...',
   *    },
   *  });
   * ```
   */
  public async fetch(params: any) {
    if (!this.fetchLib) {
      console.warn('new InteractVideo 未传入 fetchLib');
      return;
    }

    return new Promise(async (resolve, reject) => {
      try {
        let res = await this.fetchLib(params);
        if (typeof res === 'undefined') {
          return resolve(undefined);
        }
        res = JSON.parse(JSON.stringify(res));
        resolve(res);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * 拉取道具列表
   * @param vid 可选，视频vid
   * @example
   * ```javascript
   * const result = await this.bridge.invoke('engine', 'getProductList', {
   *   vid: '',
   * });
   * ```
   */
  public async getProductList(param: { vid?: string, isTest?: boolean } = {}) {
    const { result } = await this.bridge.getProductList({
      vid: param.vid || '',
      dramaId: this.dataProcessor.getMetaId(),
      isTest: param.isTest || false,
    });
    return result;
  }

  /**
   * 单点支付
   * @param productId 物品id
   * @example
   * ```javascript
   * const result = await this.bridge.invoke('engine', 'pay', {
   *   productId: 'xx',
   * });
   * ```
   */
  public async pay(param: { productId: string, isTest?: boolean }) {
    const res = await this.bridge.pay({
      dramaId: this.dataProcessor.getMetaId(),
      productId: param.productId,
      isTest: param.isTest || false,
    });
    return res;
  }

  /**
   * 查询支付状态
   * @param productId 物品id
   * @example
   * ```javascript
   * const result = await this.bridge.invoke('engine', 'getPayState', {
   *   productId: '',
   * })
   * ```
   */
  public async getPayState(param: { productId: string, isTest?: boolean }) {
    const { result } = await this.bridge.getPayState({
      dramaId: this.dataProcessor.getMetaId(),
      productId: param.productId,
      isTest: param.isTest || false,
    });
    return result;
  }

  /**
   * 获取剧id
   * @example
   * ```javascript
   * const dramaId = await this.bridge.invoke('engine', 'getMetaId')
   * ```
   */
  public getMetaId() {
    return this.dataProcessor.getMetaId();
  }

  /**
   * 隐藏直出页左上角的返回按钮
   * @example
   * ```javascript
   * this.bridge.invoke('engine', 'hideBackBtn')
   * ```
   */
  public hideBackBtn() {
    this.ivMsg.emit('hideBackBtn');
  }

  /**
   * 显示直出页左上角的返回按钮
   * @example
   * ```javascript
   * this.bridge.invoke('engine', 'showBackBtn')
   * ```
   */
  public showBackBtn() {
    this.ivMsg.emit('showBackBtn');
  }

  /**
   * 排行榜
   * @example
   * ```javascript
   * const result = await this.bridge.invoke('engine', 'getRankList', {
   *   rankId: '',
   *   pageNum: 1,
   *   pageSize: 10,
   * })
   * ```
   */
  public async getRankList(param: {
    rankId: string,
    pageNum?: number,
    pageSize?: number,
    isTest?: boolean,
  }) {
    const { rankId, pageNum, pageSize, isTest } = param;
    const { result } = await this.bridge.getRankList({
      metaId: this.dataProcessor.getMetaId(),
      rankId,
      pageNum: pageNum || 1,
      pageSize: pageSize || 10,
      isTest: isTest || false,
    });
    return result;
  }

  /**
   * 设置全局变量
   */
  public setGlobalVar(param: Record<string, any>) {
    const glabalVarMap = window.IV_GLOBAL_VAR || {};
    window.IV_GLOBAL_VAR = {
      ...glabalVarMap,
      ...param,
    };
  }

  /**
   * 获取全局变量
   */
  public getGlobalVar(key: string) {
    return (window.IV_GLOBAL_VAR || {})[key];
  }

  /**
   * 销毁
   */
  public destroy() {
    window.clearTimeout(this.preloadTimeout);
    this.msg.removeAllListeners();
    this.ivMsg.removeAllListeners();
  }

  /**
   * 播放时间更新逻辑
   * @param msTime
   */
  private timeUpdate(msTime: number) {
    if (!this.currentNode) {
      return;
    }

    const { interacts, endTime } = this.currentNode;
    if (!interacts || !interacts.length) {
      return;
    }

    if (endTime && msTime >= endTime) {
      this.player.pause();
    }
  }

  /**
   * 视频预加载
   */
  private async preloadVideo() {
    const ids: string[] = await this.dataProcessor.getNextNodeListUri(this.currentNode.id);
    if (ids.length > 0) {
      log('预加载', ids);
      // 稍微延后一点时间加载
      window.clearTimeout(this.preloadTimeout);
      this.preloadTimeout = window.setTimeout(() => {
        this.player.preload(ids);
      }, 1000);
    }
  }

  /**
   * 播放器状态逻辑处理
   * @param canAutoplay H5是否自动播放
   * @param isPlaying APP是否正在播放中，以及当前播放的地址
   */
  @methodTracker(true, trackerEnv.parent)
  private bindPlayer(canAutoplay: { result: any }, isPlaying: IVideoInfo) {
    log(`create, player status, canAutoplay: ${JSON.stringify(canAutoplay?.result)}, isPlaying: ${isPlaying}`);
    if (!isPlaying) {
      if (canAutoplay?.result) {
        this.ready();
      } else {
        this.ivMsg.emit('readyToPlay');
      }
    } else {
      this.msg.emit('videoPlay', isPlaying);
    }
  }
}

export default Interactor;
